import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { useSystemClockDraftMigration } from '../Hooks/useSystemClockSettings';
import ControllerClock from '../FormWidgets/ControllerClock';
import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import getStateManager from '../StateManager.js';
import { useSnapshot } from 'valtio';
import _ from 'lodash';

import Button from '@material-ui/core/Button';
import Tooltip from '@mui/material/Tooltip';
import MuiSwitch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';

import { useSnackbar } from 'notistack';

import { SettingActions } from '../components';
import { firmware_version_infos, statusColors } from '../Constants/constants';
import Desc from '../FormWidgets/Description';
import RootSenseFirmwareLinks from '../FormWidgets/RootSenseFirmwareLinks';
import { NormalValue } from '../FormWidgets/InfoValues';
import {
  AverageMinMaxSelect,
  DateFormatSelect,
  DisplayTimeoutSelect,
  EcAcSelect,
  EnableDisableSelect,
  FirmwareSelect,
  LanguageSelect,
  TempFormatSelect,
  VPDFormatSelect
} from '../FormWidgets/Select';
import { SettingGroup, SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import { useAuth } from '../Hooks/authHook';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { GetFirmwareUpdateComponents } from '../Utils/firmware_utils';
import { scrollToElementWhenReady } from '../Utils/scroll_utils';
import {
  systemTelemetryFields,
  controllerClockDraftFields,
  controllerClockEdit,
  controllerClockDisplayValues,
  supportsSystemClockRelocation,
  systemClockForSubmission,
  systemRestartActions,
  supportsSystemInfoControls,
  getSystemRestartDisabledReason,
  getMaincontrollerFlashHealth,
  getRootSenseLinkFirmwareVersion,
  getFirmwareUpdateOtaInfoKey,
  shouldShowFilesystemStatus
} from '../Utils/system_settings_utils';
import { getControllerApiVersion, HandleAction, isZonable, isZoned } from '../Utils/utils.js';
import { useSavedSettings, settingControls, useSettingsReadback } from './settingFunctions';
import { useTabController, useReceivedSettings } from '../Hooks/useTabController.js';
import { useFirmwareVersions } from '../Hooks/useFirmwareVersions';
import PreviewFeaturesButton from './SystemSettings/PreviewFeaturesButton';

const MIN_FW_NUM_LENGTH = 12;
const firmwareVersionStyle = { whiteSpace: 'normal', overflowWrap: 'anywhere' };

function FirmwareUpdateOtaInfo({ controller, t }) {
  const liveController = useSnapshot(controller);
  const translationKey = getFirmwareUpdateOtaInfoKey(liveController);
  if (!translationKey) return null;

  const textStyle = {
    flex: '1 0 100%',
    marginBottom: 16,
    whiteSpace: 'pre-line'
  };
  const warningStyle = {
    ...textStyle,
    color: statusColors.bad
  };
  const updateConfirmationWarning = (
    <Typography style={warningStyle}>{t('fw_update_confirm_disabled_info')}</Typography>
  );

  if (translationKey === 'fw_update_ota_info_505x') {
    return (
      <>
        <Typography style={warningStyle}>{t(translationKey)}</Typography>
        <Typography style={textStyle}>{t('fw_update_ota_info_505x_details')}</Typography>
        {updateConfirmationWarning}
      </>
    );
  }

  return (
    <>
      <Typography style={warningStyle}>{t(translationKey)}</Typography>
      {updateConfirmationWarning}
    </>
  );
}

function hasFSStatusValue(value) {
  return value !== undefined && value !== null && value !== -1;
}

function getFSStatusDisplayValue(value) {
  if (!hasFSStatusValue(value)) return -1;
  return value ? 'OK' : 'ERROR';
}

export default function SystemSettings({ ...other }) {
  let { enqueueSnackbar } = useSnackbar();

  let { t } = useTranslation();
  let params = useParams();
  const { hash } = useLocation();
  let t_id = 'system';
  let stateManager = getStateManager();
  const { controller, controllerSnapshot } = useTabController(params);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let settings = controllerSnapshot.settings;

  const hasSystemClock = supportsSystemClockRelocation(controllerSnapshot);
  // Native Industrial Pro uses forwarded JSON and its own API history.
  const vpdFormatSupported = controllerSnapshot.type === 6000 ||
    getControllerApiVersion(controllerSnapshot) >= 'ADCF';
  const action_write_access = controllerSnapshot.access_rights.timing?.write === true;
  const write_access = hasSystemClock
    ? controllerSnapshot.access_rights.system?.write === true : action_write_access;
  useSystemClockDraftMigration(controller, controller, !other.recoveryMode);

  const clockBaseline = () => ({ ...controller.settings.system,
    ...(hasSystemClock ? controllerClockDisplayValues(controller) : {}) });
  const [values, setValues] = useState(() => other.recoveryMode ? {} : clockBaseline());
  const [dateInputValid, setDateInputValid] = useState(true);
  const [dateResetKey, setDateResetKey] = useState(0);
  const received = useReceivedSettings(controller, 'system');
  const readback = useSettingsReadback(controller, t_id, received, values, setValues, {
    enabled: !other.recoveryMode,
    canRetain: write_access && !other.recoveryMode,
    preservePaths: controllerClockDraftFields
  });
  // Retain actual clock/diagnostic readback in the generic optimistic baseline.
  // The existing clock Save only reports submission, not RTC confirmation.
  const controls = settingControls(controller, controller, t_id, values, setValues, {
    ...readback,
    getBaseline: clockBaseline,
    canEdit: () => write_access,
    submit: () => {
      if (!write_access || !dateInputValid) return false;
      const readbackFields = hasSystemClock
        ? [...systemTelemetryFields, ...controllerClockDraftFields] : systemTelemetryFields;
      const sent = stateManager.saveSettings(controller, controller, {
        ..._.omit(values, readbackFields),
        ..._.pick(controller.settings.system, readbackFields)
      }, t_id, () => _.omit(hasSystemClock ? systemClockForSubmission(values) : values,
        systemTelemetryFields));
      if (sent && hasSystemClock) setValues((current) => ({ ...current,
        _date_changed: false, _time_changed: false }));
      return sent;
    }
  });
  const reset = () => {
    controls.resetSettings();
    setDateResetKey((key) => key + 1);
  };
  const editClock = (value, date) => controls.setValue(...controllerClockEdit(value, date));
  const rootSenseFirmware = getRootSenseLinkFirmwareVersion({
    type: controllerSnapshot.type, api_version: controllerSnapshot.api_version, settings
  });
  // Recovery does not edit System settings or restore/discard their saved draft.
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(
    controller,
    controls,
    t_id,
    other.recoveryMode ? () => false : null
  );

  const refreshFSStatus = () => {
    stateManager.wsManager.sendWSMsg(controller, 'get_fs_status');
  };

  // firmware updates.
  const auth = useAuth();
  const firmware_versions = useFirmwareVersions(connectionStatus);
  const [firmware_version, setFirmwareVersion] = useState(-1);
  useEffect(() => {
    if (hash !== '#firmware') return undefined;

    return scrollToElementWhenReady('firmware', { behavior: 'smooth', block: 'start' });
  }, [hash]);

  useEffect(() => {
    if (auth.user) {
      // Flash
      const prevFlashValue = JSON.parse(localStorage.getItem('prevFlashValue')) || {
        triggered_flash: false,
        free: controller.fs_status.free
      };
      const triggered_flash = prevFlashValue.triggered_flash;
      const prevFree = prevFlashValue.free;

      console.log("[fw-ok] checking status")
      if (controller.connection.status === 'good') {
        // Flash
        if (triggered_flash && controller.fs_status.free > prevFree - 1) {
          enqueueSnackbar(t('successful_erase_flash'), { variant: 'success' });
          localStorage.setItem(
            'prevFlashValue',
            JSON.stringify({
              triggered_flash: false,
              free: controller.fs_status.free
            })
          );
        }
      } 
    }
  }, [connectionStatus]);

  const callAction = (type, value) => {
    const zone_id = 0xfe;
    HandleAction(stateManager.wsManager, controller, type, value, zone_id);
  };

  const callFirmwareAction = (enqueueSnackbar, update_gc_firmware, update_wifi_firmware) => {
    if (!controller.access_rights.timing?.write) return;
    if (!other.recoveryMode && controller.connection?.status !== 'good') return;
    // Firmware
    if ((controller?.type ?? 5053) <= 5155) {
      const firmware_data = firmware_versions[controller?.type][firmware_version];
      if (firmware_data && controller.connection.status !== 'bad') {
        if (update_gc_firmware === 1 && update_wifi_firmware === 1) {
          enqueueSnackbar(t('start_firmware_update'), { variant: 'info' });
          stateManager.wsManager.sendWSMsg(controller, 'do_firmware_update', firmware_data);
        } else if (update_gc_firmware === 1 && update_wifi_firmware === 0) {
          enqueueSnackbar(t('start_gc_firmware_update'), { variant: 'info' });
          stateManager.wsManager.sendWSMsg(controller, 'do_gc_firmware_update', firmware_data);
        } else if (update_gc_firmware === 0 && update_wifi_firmware === 1) {
          enqueueSnackbar(t('start_wifi_firmware_update'), { variant: 'info' });
          stateManager.wsManager.sendWSMsg(controller, 'do_esp32_firmware_update', firmware_data);
        }
      }
    } // industrial pro
    else {
      const firmware_data = values.firmware_updates[firmware_version];

      if (firmware_data && controller.connection !== 'bad') {
		enqueueSnackbar(t('start_gc_firmware_update'), { variant: 'info' });
	    stateManager.wsManager.sendWSMsg(controller, 'do_firmware_update', firmware_data, true);
      }
    }
    setFirmwareVersion(-1);
  };

  // (Auto-) Load saved settings.

  const [open_wifi_dialog, setOpenWifiDialog] = useState(false);
  const openWifiDialog = () => {
    setOpenWifiDialog(true);
  };
  const closeWifiDialog = () => {
    setOpenWifiDialog(false);
  };

  const [open_project_dialog, setOpenProjectDialog] = useState(false);
  const openProjectDialog = () => {
    setOpenProjectDialog(true);
  };
  const closeProjectDialog = () => {
    setOpenProjectDialog(false);
  };

  const [open_confirm_fw_update_dialog, setOpenConfirmFWUpdateDialog] = useState(false);
  const openConfirmFwUpdateDialog = () => {
    setOpenConfirmFWUpdateDialog(true);
  };
  const closeConfirmFwDialogDialog = () => {
    setOpenConfirmFWUpdateDialog(false);
  };
  const [open_fw_update_notice_dialog, setOpenFWUpdateNoticeDialog] = useState(false);
  const openFwUpdateNoticeDialog = () => {
    setOpenFWUpdateNoticeDialog(true);
  };
  const closeFwDialogNoticeDialog = () => {
    setOpenFWUpdateNoticeDialog(false);
  };

  const [confirm_action_dialog_infos, setOpenConfirmActionDialog] = useState({
    open: false,
    type: '',
    value: undefined
  });
  const openConfirmActionDialog = (type, value) => {
    setOpenConfirmActionDialog({ open: true, type: type, value: value });
  };
  const closeConfirmActionDialog = () => {
    setOpenConfirmActionDialog({ open: false, type: '', value: -1 });
  };

  const restartAction = systemRestartActions.includes(confirm_action_dialog_infos.type);
  const confirmActionDisabledReason = restartAction
    ? t(getSystemRestartDisabledReason(controllerSnapshot, confirm_action_dialog_infos.type))
    : connectionDisabledReason;
  const submitConfirmedAction = () => {
    const { type, value } = confirm_action_dialog_infos;
    if (systemRestartActions.includes(type)) {
      if (getSystemRestartDisabledReason(controller, type)) return;
      stateManager.wsManager.sendWSMsg(controller, 'action',
        { id: type, value: 0, zone_id: 0xfe }, false, isZoned(controller), false);
    } else {
      if (controller.connection?.status !== 'good') return;
      HandleAction(stateManager.wsManager, controller, type, value, 0xfe, t, enqueueSnackbar);
    }
  };

  const [fwUpdateGc, setFwUpdateGc] = useState(null);
  const [fwUpdateWifi, setFwUpdateWifi] = useState(null);

  let fwDisabledReason = '';

  if (connectionStatus !== 'good') {
    fwDisabledReason = t('disabled_fw_btn_waiting_for_connection');
  } else if (firmware_version === -1) {
    fwDisabledReason = t('disabled_fw_btn_no_fw_selected');
  } else if (values.fw_version_number ===
      firmware_versions[controllerSnapshot.type]?.[0]?.gc_firmware &&
    values.esp32_firmware_name ===
      firmware_versions[controllerSnapshot.type]?.[0]?.esp_firmware) {
    fwDisabledReason = t('disabled_fw_btn_up_to_date');
  } else if (!action_write_access) {
    fwDisabledReason = t('disabled_fw_btn_no_write_access');
  } 
  const fwBtnIsDisabled = fwDisabledReason !== '';

  return (
    <>
      <WifiDialog
        t={t}
        open={open_wifi_dialog}
        handleClose={closeWifiDialog}
        stateManager={stateManager}
        controller={controller}
      />
      <ProjectDialog
        t={t}
        open={open_project_dialog}
        handleClose={closeProjectDialog}
        stateManager={stateManager}
        controller={controller}
      />
      <ConfirmActionDialog
        t={t}
        open={confirm_action_dialog_infos.open}
        handleClose={closeConfirmActionDialog}
        action_type={confirm_action_dialog_infos.type}
        disabledReason={confirmActionDisabledReason}
        onAction={submitConfirmedAction}
      />

      <ConfirmFirmwareUpdateDialog
        t={t}
        stateManager={stateManager}
        controller={controller}
        firmware_versions={firmware_versions}
        firmware_version={firmware_version}
        recoveryMode={other.recoveryMode}
        writeAccess={action_write_access}
        open={open_confirm_fw_update_dialog}
        handleClose={closeConfirmFwDialogDialog}
        handleFirwareUpdate={callFirmwareAction}
        handleFirwareUpdateNotice={(update_gc_firmware, update_wifi_firmware) => {
          setFwUpdateGc(update_gc_firmware);
          setFwUpdateWifi(update_wifi_firmware);
          openFwUpdateNoticeDialog();
        }}
      />
      <FirmwareUpdateNoticeDialog
        t={t}
        open={open_fw_update_notice_dialog}
        handleClose={closeFwDialogNoticeDialog}
        update_gc_firmware={fwUpdateGc}
        update_wifi_firmware={fwUpdateWifi}
      />

      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      {other.recoveryMode ? (
        <>
          <span id="firmware" style={{ display: 'block', scrollMarginTop: '10rem' }} />
          <SettingGroup controller={controllerSnapshot} id={'firmware_hl'}>
            <FirmwareUpdateOtaInfo controller={controller} t={t} />
            <ValueBox controller={controllerSnapshot} id={'system_firmware_update'}>
              <Typography gutterBottom>{t('firmware_update')}</Typography>
              {controllerSnapshot?.type <= 5155 && (
                <FirmwareSelect
                  controller={controllerSnapshot}
                  value={firmware_version}
                  versions={firmware_versions[controllerSnapshot?.type]}
                  name="firmware_update"
                  id="firmware_update"
                  onChange={(event, value) => setFirmwareVersion(event.target.value)}
                />
              )}
              {controllerSnapshot?.type === 6000 && (
                <FirmwareSelect
                  value={firmware_version}
                  controller={controllerSnapshot}
                  versions={controllerSnapshot.settings.system.firmware_updates}
                  name="firmware_update"
                  id="firmware_update"
                  onChange={(event, value) => setFirmwareVersion(event.target.value)}
                />
              )}
            </ValueBox>
            <div>
              <Button
                color="primary"
                disabled={
                  firmware_version === -1 || !action_write_access || controllerSnapshot.connection.status === 'bad'
                }
                onClick={() => openConfirmFwUpdateDialog()}
              >
                {t('apply_firmware_update')}
              </Button>
              <br />
              {(firmware_version_infos[controllerSnapshot?.type] ?? []).map((entry) => (
                <>
                  <a
                    href={entry.link}
                    style={{ color: statusColors.good }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {entry.text} <PictureAsPdfIcon />
                  </a>
                  <br />
                </>
              ))}
            </div>
          </SettingGroup>
        </>
      ) : (
        <>
          {write_access && (
            <SettingActions
              title={connectionDisabledReason}
              on_default={connectionStatus !== 'good' || !dateInputValid}
              onReset={reset}
              onSave={controls.saveSettings}
            />
          )}

          {/* The clock follows live RTC readback itself; unedited groups are not submitted. */}
          {hasSystemClock && <ControllerClock controller={controllerSnapshot} liveController={controller} values={values}
            disabled={!write_access} resetKey={dateResetKey} onValidityChange={setDateInputValid}
            onDateChange={(date) => editClock(date, true)}
            onTimeChange={(time) => editClock(time, false)} />}

          <SettingGroup controller={controllerSnapshot} id={'formats'}>
            <ValueBox controller={controllerSnapshot} id={'system_temp_format'}>
              <Typography gutterBottom>
                <Desc id="temp_format" name="Temperature Format" />
              </Typography>
              <TempFormatSelect controls={controls}
                value={values.temp_format}
                controller_value={controllerSnapshot.settings.system.temp_format}
                name="temp_format"
                onChange={(event, value) => controls.setValue('temp_format', event.target.value)}
                disabled={!write_access}
              />
            </ValueBox>
            {vpdFormatSupported && (
              <ValueBox controller={controllerSnapshot} id={'system_vpd_format'}>
                <Typography gutterBottom>
                  <Desc id="vpd_format" name="VPD Format" />
                </Typography>
                <VPDFormatSelect
                  controls={controls}
                  value={values.vpd_format}
                  controller_value={controllerSnapshot.settings.system.vpd_format}
                  name="vpd_format"
                  inputProps={{ 'aria-label': t('vpd_format') }}
                  onChange={(event, value) => controls.setValue('vpd_format', event.target.value)}
                  disabled={!write_access}
                />
              </ValueBox>
            )}
            <ValueBox controller={controllerSnapshot} id={'system_date_format'}>
              <Typography gutterBottom>
                <Desc id="date_format" name="Date Format" />
              </Typography>
              <DateFormatSelect controls={controls}
                value={values.date_format}
                controller_value={controllerSnapshot.settings.system.date_format}
                name="date_format"
                onChange={(event, value) => controls.setValue('date_format', event.target.value)}
                disabled={!write_access}
              />
            </ValueBox>
          </SettingGroup>

          <SettingGroup controller={controllerSnapshot} id={'controller_menu_settings'}>
            <ValueBox controller={controllerSnapshot} id={'system_language'}>
              <Typography gutterBottom>
                <Desc id="language" name="Language" />
              </Typography>
              <LanguageSelect controls={controls}
                value={values.language}
                controller_value={controllerSnapshot.settings.system.language}
                name="language"
                onChange={(event, value) => controls.setValue('language', event.target.value)}
                disabled={!write_access}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_display_timeout_gc'}>
              <Typography gutterBottom>
                <Desc id="display_timeout_gc" name="Display Timeout" />
              </Typography>
              <DisplayTimeoutSelect controls={controls}
                value={values.display_timeout}
                controller_value={controllerSnapshot.settings.system.display_timeout}
                name="display_timeout"
                onChange={(event, value) =>
                  controls.setValue('display_timeout', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
          </SettingGroup>

          <SettingGroup controller={controllerSnapshot} id={'menu_settings'}>
            <ValueBox controller={controllerSnapshot} id={'system_fan_type_select'}>
              <Typography gutterBottom>
                <Desc id="fan_type_select" name="Display Timeout" />
              </Typography>
              <EcAcSelect controls={controls}
                value={values.fan_type_select}
                controller_value={controllerSnapshot.settings.system.fan_type_select}
                name="fan_type_select"
                onChange={(event, value) =>
                  controls.setValue('fan_type_select', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
          </SettingGroup>

          <SettingGroup controller={controllerSnapshot} id={'multi_sensor_handling'}>
            <ValueBox controller={controllerSnapshot} id={'system_multi_sensor_handling_rh'}>
              <Typography gutterBottom>{t('multi_sensor_handling_rh')}</Typography>
              <AverageMinMaxSelect controls={controls}
                value={values.multi_sensor_handling_rh}
                controller_value={controllerSnapshot.settings.system.multi_sensor_handling_rh}
                name="multi_sensor_handling_rh"
                onChange={(event, value) =>
                  controls.setValue('multi_sensor_handling_rh', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_multi_sensor_handling_temp'}>
              <Typography gutterBottom>
                <Desc id="multi_sensor_handling_temp" name="Multi Sensor Handling Temperature" />
              </Typography>
              <AverageMinMaxSelect controls={controls}
                value={values.multi_sensor_handling_temp}
                controller_value={controllerSnapshot.settings.system.multi_sensor_handling_temp}
                name="multi_sensor_handling_temp"
                onChange={(event, value) =>
                  controls.setValue('multi_sensor_handling_temp', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_multi_sensor_handling_co2'}>
              <Typography gutterBottom>
                <Desc id="multi_sensor_handling_co2" name="Multi Sensor Handling CO2" />
              </Typography>
              <AverageMinMaxSelect controls={controls}
                value={values.multi_sensor_handling_co2}
                controller_value={controllerSnapshot.settings.system.multi_sensor_handling_co2}
                name="multi_sensor_handling_co2"
                onChange={(event, value) =>
                  controls.setValue('multi_sensor_handling_co2', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_multi_sensor_handling_pressure'}>
              <Typography gutterBottom>
                <Desc id="multi_sensor_handling_pressure" name="Multi Sensor Handling Pressure" />
              </Typography>
              <AverageMinMaxSelect controls={controls}
                value={values.multi_sensor_handling_pressure}
                controller_value={controllerSnapshot.settings.system.multi_sensor_handling_pressure}
                name="multi_sensor_handling_pressure"
                onChange={(event, value) =>
                  controls.setValue('multi_sensor_handling_pressure', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_multi_sensor_handling_heating_mat'}>
              <Typography gutterBottom>
                <Desc
                  id="multi_sensor_handling_heating_mat"
                  name="Multi Sensor Handling Heating Mat"
                />
              </Typography>
              <AverageMinMaxSelect controls={controls}
                value={values.multi_sensor_handling_heating_mat}
                controller_value={controllerSnapshot.settings.system.multi_sensor_handling_heating_mat}
                name="multi_sensor_handling_heating_mat"
                onChange={(event, value) =>
                  controls.setValue('multi_sensor_handling_heating_mat', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_multi_sensor_handling_leaf'}>
              <Typography gutterBottom>
                <Desc id="multi_sensor_handling_leaf" name="Multi Sensor Handling Leaf" />
              </Typography>
              <AverageMinMaxSelect controls={controls}
                value={values.multi_sensor_handling_leaf}
                controller_value={controllerSnapshot.settings.system.multi_sensor_handling_leaf}
                name="multi_sensor_handling_leaf"
                onChange={(event, value) =>
                  controls.setValue('multi_sensor_handling_leaf', event.target.value)
                }
                disabled={!write_access}
              />
            </ValueBox>
          </SettingGroup>

          {!isZonable(controllerSnapshot) && (
            <SettingGroupGrid controller={controllerSnapshot} id={'sensor_calibration_hl'}>
              <ValueBox controller={controllerSnapshot} id={'system_pressure_auto_cal'}>
                <Typography gutterBottom>
                  <Desc id="pressure_auto_cal" />
                </Typography>
                <EnableDisableSelect controls={controls}
                  value={values.pressure_auto_cal}
                  controller_value={controllerSnapshot.settings.system.pressure_auto_cal}
                  name="pressure_auto_cal"
                  onChange={(event, value) =>
                    controls.setValue('pressure_auto_cal', event.target.value)
                  }
                  disabled={!write_access}
                />
              </ValueBox>
              <ActionButton
                title={
                  connectionDisabledReason ||
                  (!action_write_access ? t('disabled_action_no_write_access') : '')
                }
                color="primary"
                onClick={(event) => callAction('action_calibrate_pressure_trigger')}
                disabled={connectionStatus !== 'good' || !action_write_access}
              >
                {t('calibrate_pressure_trigger')}
              </ActionButton>

              <ShowSetting
                id="filter_val_sensor_co2"
                controller={controllerSnapshot}
                cur_value={values.filter_val_sensor_co2}
                controller_value={controllerSnapshot.settings.system.filter_val_sensor_co2}
                controls={controls}
                disabled={!write_access}
              />
              <ActionButton
                title={
                  connectionDisabledReason ||
                  (!action_write_access ? t('disabled_action_no_write_access') : '')
                }
                color="primary"
                onClick={() =>
                  callAction('action_transmit_filter_sensor', values.filter_val_sensor_co2)
                }
                disabled={connectionStatus !== 'good' || !action_write_access}
              >
                {t('transmit')}
              </ActionButton>
              <ShowSetting
                id="calib_val_sensor_co2"
                controller={controllerSnapshot}
                cur_value={values.calib_val_sensor_co2}
                controller_value={controllerSnapshot.settings.system.calib_val_sensor_co2}
                controls={controls}
                disabled={!write_access}
              />
              <ActionButton
                title={
                  connectionDisabledReason ||
                  (!action_write_access ? t('disabled_action_no_write_access') : '')
                }
                color="primary"
                onClick={() =>
                  callAction('action_transmit_calib_filter_sensor_co2', values.calib_val_sensor_co2)
                }
                disabled={connectionStatus !== 'good' || !action_write_access}
              >
                {t('transmit')}
              </ActionButton>
            </SettingGroupGrid>
          )}

          <SettingGroupGrid>
            <ActionButton
              title={
                connectionDisabledReason ||
                (!action_write_access ? t('disabled_action_no_write_access') : '')
              }
              onClick={() => openConfirmActionDialog('action_restore_factory_default', undefined)}
              style={{ color: statusColors.bad, border: '1px solid' }}
              disabled={connectionStatus !== 'good' || !action_write_access}
            >
              {t('restore_factory_default')}
            </ActionButton>
          </SettingGroupGrid>

          <span id="firmware" style={{ display: 'block', scrollMarginTop: '10rem' }} />
          <SettingGroup controller={controllerSnapshot} id={'firmware_hl'}>
            <FirmwareUpdateOtaInfo controller={controller} t={t} />
            <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
              <ValueBox controller={controllerSnapshot} id={'system_current_firware_version_main_controller'} style={firmwareVersionStyle}>
                <Typography gutterBottom>
                  <strong>{t('current_firware_version_main_controller')}:</strong>
                </Typography>
                <label style={{ whiteSpace: 'pre-line' }}>
                  <i>{controllerSnapshot.settings.system.fw_version_number}</i>
                </label>
              </ValueBox>
              <ValueBox controller={controllerSnapshot} id={'system_current_firware_version_wifi'} style={firmwareVersionStyle}>
                <Typography gutterBottom>
                  <strong>{t('current_firware_version_wifi')}:</strong>
                </Typography>
                <label style={{ whiteSpace: 'pre-line' }}>
                  <i>{controllerSnapshot.settings.system.esp32_firmware_name}</i>
                </label>
              </ValueBox>
              {supportsSystemInfoControls(controllerSnapshot) && (
                <ValueBox controller={controllerSnapshot} id={'system_esp32_chip_type'}>
                  <Typography gutterBottom><strong>{t('esp32_chip_type')}:</strong></Typography>
                  <i>{settings.system.esp32_chip_type ?? t('unknown')}</i>
                </ValueBox>
              )}
            </div>
            <ValueBox controller={controllerSnapshot} id={'system_firmware_update'}>
              <Typography gutterBottom>{t('firmware_update')}</Typography>
              {controllerSnapshot?.type <= 5155 && (
                <FirmwareSelect
                  value={firmware_version}
                  versions={firmware_versions[controllerSnapshot?.type]}
                  esp_fw={controllerSnapshot?.settings?.system?.esp32_firmware_name}
                  gc_fw={controllerSnapshot?.settings?.system?.fw_version_number}
                  cont_type={controllerSnapshot?.type}
                  name="firmware_update"
                  id="firmware_update"
                  onChange={(event, value) => setFirmwareVersion(event.target.value)}
                  disabled={
                    values.fw_version_number ===
                      firmware_versions[controllerSnapshot?.type]?.[0]?.gc_firmware &&
                    values.esp32_firmware_name ===
                      firmware_versions[controllerSnapshot?.type]?.[0]?.esp_firmware
                  }
                />
              )}
              {controllerSnapshot?.type === 6000 && (
                <FirmwareSelect
                  value={firmware_version}
                  versions={controllerSnapshot.settings.system.firmware_updates}
                  esp_fw={controllerSnapshot?.settings?.system?.esp32_firmware_name}
                  gc_fw={controllerSnapshot?.settings?.system?.fw_version_number}
                  cont_type={controllerSnapshot?.type}
                  name="firmware_update"
                  id="firmware_update"
                  onChange={(event, value) => setFirmwareVersion(event.target.value)}
                />
              )}
            </ValueBox>
            <div>
              <Tooltip
                enterTouchDelay={0}
                leaveTouchDelay={3000}
                title={fwDisabledReason}
              >
                <span>
                  <Button
                    color="primary"
                    disabled={fwBtnIsDisabled}
                    onClick={openConfirmFwUpdateDialog}
                  >
                    {t('apply_firmware_update')}
                  </Button>
                </span>
              </Tooltip>
              <br />
              {(firmware_version_infos[controllerSnapshot?.type] ?? []).map((entry) => (
                <>
                  <a
                    href={entry.link}
                    style={{ color: statusColors.good }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {entry.text} <PictureAsPdfIcon />
                  </a>
                  <br />
                </>
              ))}
            </div>
          </SettingGroup>

          {rootSenseFirmware !== null && (
            <SettingGroup controller={controllerSnapshot} id={'connected_extensions_hl'} label={'connected_extensions_hl'}>
              <ValueBox controller={controllerSnapshot} id={'system_current_firmware_version_root_sense_link'} style={firmwareVersionStyle}>
                <Typography gutterBottom>
                  <strong>{t('current_firmware_version_root_sense_link')}:</strong>
                </Typography>
                <i>{rootSenseFirmware || t('unknown')}</i>
                <RootSenseFirmwareLinks />
              </ValueBox>
            </SettingGroup>
          )}

          {supportsSystemInfoControls(controllerSnapshot) && (
            <SettingGroup controller={controllerSnapshot} id={'maincontroller_flash_hl'}>
              <ValueBox controller={controllerSnapshot} id={'system_maincontroller_flash_health'}>
                <Typography gutterBottom><Desc id={'maincontroller_flash_health'} /></Typography>
                <NormalValue name="maincontroller_flash_health"
                  style={{ fontSize: '1rem' }}
                  value={getMaincontrollerFlashHealth(settings.system.maincontroller_flash_health, t)} />
              </ValueBox>
              <ValueBox controller={controllerSnapshot} id={'system_maincontroller_flash_status'}>
                <Typography gutterBottom><Desc id={'maincontroller_flash_status'} /></Typography>
                <NormalValue name="maincontroller_flash_status"
                  style={{ fontSize: '1rem' }}
                  value={settings.system.maincontroller_flash_status ?? -1} />
              </ValueBox>
            </SettingGroup>
          )}

          <SettingGroup
            controller={controllerSnapshot}
            id={'fs_status_hl'}
            visible={shouldShowFilesystemStatus(controllerSnapshot)}
          >
            <Typography style={{ flex: '1 0 100%', marginBottom: 16 }}>
              {t('fs_status_refresh_info')}
            </Typography>
            <ValueBox controller={controllerSnapshot} id={'system_total_size'}>
              <Typography gutterBottom>
                <Desc id={'total_size'} />
              </Typography>
              {hasFSStatusValue(controllerSnapshot.fs_status.total_size) &&
              (controllerSnapshot.fs_status.total_size / (1024 * 1024)).toFixed(1) >= 100.0 ? (
                <NormalValue
                  name="total_size"
                  value={(controllerSnapshot.fs_status.total_size / (1024 * 1024 * 1024)).toFixed(1)}
                  unit="GB"
                  style={{ fontSize: '1.3rem' }}
                />
              ) : (
                <NormalValue
                  name="total_size"
                  value={
                    hasFSStatusValue(controllerSnapshot.fs_status.total_size)
                      ? (controllerSnapshot.fs_status.total_size / (1024 * 1024)).toFixed(1)
                      : -1
                  }
                  unit="MB"
                  style={{ fontSize: '1.3rem' }}
                />
              )}
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_free'}>
              <Typography gutterBottom>
                <Desc id={'free'} />
              </Typography>
              <NormalValue
                name="free"
                value={
                  hasFSStatusValue(controllerSnapshot.fs_status.free)
                    ? controllerSnapshot.fs_status.free
                    : -1
                }
                unit="%"
                style={{ fontSize: '1.3rem' }}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_flash_status'}>
              <Typography gutterBottom>
                <Desc id={'flash_status'} />
              </Typography>
              <NormalValue
                name="fs_status"
                value={getFSStatusDisplayValue(controllerSnapshot.fs_status.flash_status)}
                style={{ fontSize: '1.3rem' }}
              />
            </ValueBox>
            <ValueBox controller={controllerSnapshot} id={'system_fs_status'}>
              <Typography gutterBottom>
                <Desc id={'fs_status'} />
              </Typography>
              <NormalValue
                name="fs_status"
                value={getFSStatusDisplayValue(controllerSnapshot.fs_status.fs_status)}
                style={{ fontSize: '1.3rem' }}
              />
            </ValueBox>
            <div>
              <Button color="primary" onClick={() => refreshFSStatus()}>
                {t('refresh')}
              </Button>
              <br />
              <ActionButton
                title={
                  connectionDisabledReason ||
                  (!action_write_access ? t('disabled_action_no_write_access') : '')
                }
                style={{ color: statusColors.bad, border: '1px solid' }}
                disabled={connectionStatus !== 'good' || !action_write_access}
                onClick={() => openConfirmActionDialog('action_erase_flash', undefined)}
              >
                {t('erase_flash')}
              </ActionButton>
            </div>
          </SettingGroup>
          <SettingGroup controller={controllerSnapshot} id={'wifi_options'}>
            <ActionButton
              title={
                connectionDisabledReason ||
                (!action_write_access ? t('disabled_action_no_write_access') : '')
              }
              color="primary"
              onClick={openWifiDialog}
              disabled={connectionStatus !== 'good' || !action_write_access}
            >
              {t('change_wifi_options')}
            </ActionButton>
          </SettingGroup>
          <SettingGroup controller={controllerSnapshot} id={'project_settings'}>
            <Button color="primary" onClick={openProjectDialog} disabled={!action_write_access}>
              {t('toggle_project_restrictions')}
            </Button>
            <PreviewFeaturesButton />
          </SettingGroup>

          {write_access && (
            <SettingActions
              title={connectionDisabledReason}
              on_default={connectionStatus !== 'good' || !dateInputValid}
              onReset={reset}
              onSave={controls.saveSettings}
            />
          )}
        </>
      )}
      {supportsSystemInfoControls(controllerSnapshot) && (
        <SettingGroup controller={controllerSnapshot} id={'system_restart_hl'}>
          {systemRestartActions.map((action) => {
            const reason = getSystemRestartDisabledReason(controllerSnapshot, action);
            return <ActionButton key={action} color="primary" disabled={Boolean(reason)}
              title={reason ? t(reason) : ''} onClick={() => openConfirmActionDialog(action)}>
              {t(action)}
            </ActionButton>;
          })}
        </SettingGroup>
      )}
    </>
  );
}

function ConfirmActionDialog({
  t,
  open,
  handleClose,
  action_type,
  disabledReason,
  onAction
}) {
  const doAction = (event) => {
    if (disabledReason) return;
    onAction();
    handleClose();
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={Boolean(disabledReason)}
      open={open}
      onClose={handleClose}
      onEnterAction={doAction}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('confirm_action_title')}</DialogTitle>
      <DialogContent>{t(action_type + '_text')}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <ActionButton
          title={disabledReason}
          disabled={Boolean(disabledReason)}
          onClick={doAction}
          color="primary"
        >
          {t('apply')}
        </ActionButton>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function ConfirmFirmwareUpdateDialog({
  t,
  stateManager,
  controller,
  firmware_versions,
  firmware_version,
  recoveryMode,
  writeAccess,
  open,
  handleClose,
  handleFirwareUpdate,
  handleFirwareUpdateNotice
}) {
  const { disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);
  let { enqueueSnackbar } = useSnackbar();

  const selectedFirmware = firmware_versions[controller?.type]?.[firmware_version];
  const updateComponents = GetFirmwareUpdateComponents(
    controller?.settings?.system?.fw_version_number,
    controller?.settings?.system?.esp32_firmware_name,
    selectedFirmware,
    controller?.type
  );
  const shouldUpdateGcFirmware = !recoveryMode && updateComponents.gc;
  const shouldUpdateEspFirmware = !recoveryMode && updateComponents.esp;

  // Set firmware update values
  const [update_gc_firmware, setUpdateGcFirmware] = useState(Number(shouldUpdateGcFirmware));
  const [update_wifi_firmware, setUpdateWifiFirmware] = useState(Number(shouldUpdateEspFirmware));
  const liveController = useSnapshot(controller);
  const canApply =
    writeAccess &&
    (recoveryMode
      ? liveController.connection.status !== 'bad' &&
        (update_gc_firmware === 1 || update_wifi_firmware === 1)
      : liveController.connection.status === 'good');

  useEffect(() => {
    setUpdateGcFirmware(Number(shouldUpdateGcFirmware));
    setUpdateWifiFirmware(Number(shouldUpdateEspFirmware));
  }, [ shouldUpdateEspFirmware, shouldUpdateGcFirmware ]);

  const doAction = (event) => {
    handleFirwareUpdate(enqueueSnackbar, update_gc_firmware, update_wifi_firmware);
    handleClose();
  };

  const applyFirmwareUpdate = () => {
    if (!canApply) return;
    doAction();
    handleFirwareUpdateNotice(update_gc_firmware, update_wifi_firmware);
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={applyFirmwareUpdate}
      enterActionDisabled={!canApply}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('confirm_firmware_update_title')}</DialogTitle>
      <DialogContent>
        {t('confirm_firmware_update_text')}
        <br />
        <br />
        {/* Recovery firmware update */}
        {recoveryMode && (
          <>
            <Typography gutterBottom>
              <MuiSwitch
                checked={update_gc_firmware === 1}
                onClick={(event) => {
                  setUpdateGcFirmware(event.target.checked ? 1 : 0);
                }}
                disabled={firmware_versions[controller?.type]?.[firmware_version]?.gc_firmware === "---"}
                color="primary"
              />
              <Desc id="update_gc_firmware" />
            </Typography>
            <Typography gutterBottom>
              <MuiSwitch
                checked={update_wifi_firmware === 1}
                onClick={(event) => {
                  setUpdateWifiFirmware(event.target.checked ? 1 : 0);
                }}
                disabled={firmware_versions[controller?.type]?.[firmware_version]?.esp_firmware === "---"}
                color="primary"
              />
              <Desc id="update_wifi_firmware" />
            </Typography>
          </>
        )}
        {/* Normal firmware update */}
        {!recoveryMode && (
          <>
            <Typography gutterBottom>
              <MuiSwitch
                checked={update_gc_firmware === 1}
                onClick={(event) => {
                  setUpdateGcFirmware(event.target.checked ? 1 : 0);
                }}
                disabled={controller?.type !== 5155}
                color="primary"
              />
              <Desc id="update_gc_firmware" />
            </Typography>
            <Typography gutterBottom>
              <MuiSwitch
                checked={update_wifi_firmware === 1}
                onClick={(event) => {
                  setUpdateWifiFirmware(event.target.checked ? 1 : 0);
                }}
                disabled={controller?.type !== 5155}
                color="primary"
              />
              <Desc id="update_wifi_firmware" />
            </Typography>
          </>
        )}
        {/* End Normal firmware update */}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Tooltip
          enterTouchDelay={0}
          leaveTouchDelay={3000}
          title={recoveryMode ? '' : connectionDisabledReason}
        >
          <span>
            <Button
              onClick={applyFirmwareUpdate}
              color="primary"
              disabled={!canApply}
            >
              {t('apply')}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function FirmwareUpdateNoticeDialog({
  t,
  open,
  handleClose,
  update_gc_firmware,
  update_wifi_firmware
}) {
  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('firmware_update_notice_title')}</DialogTitle>
      <div style={{ textAlign: 'center' }}>
        {
          <img
            src={'https://docs.growcontrol.de/Attention_YE.png'}
            alt={'https://docs.growcontrol.de/Attention_YE.png'}
            style={{ width: '30%' }}
          />
        }
      </div>
      {update_gc_firmware === 1 && update_wifi_firmware === 1 && (
        <DialogContent style={{ whiteSpace: 'pre-line' }}>
          {t('firmware_update_notice_text')}
        </DialogContent>
      )}
      {update_gc_firmware === 1 && update_wifi_firmware === 0 && (
        <DialogContent style={{ whiteSpace: 'pre-line' }}>
          {t('gc_firmware_update_notice_text')}
        </DialogContent>
      )}
      {update_gc_firmware === 0 && update_wifi_firmware === 1 && (
        <DialogContent style={{ whiteSpace: 'pre-line' }}>
          {t('wifi_firmware_update_notice_text')}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('close')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function WifiDialog({ t, open, handleClose, stateManager, controller }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const [name, setName] = useState(null);
  const [password, setPassword] = useState(null);
  const [msg, setMsg] = useState('');

  const changeWifi = (event) => {
    if (controller.connection?.status !== 'good') return;
    let data = { password: password, name: name };
    if (password === null || name === null)
      setMsg('All fields (password and name) must be filled.');
    else if (data.password.length < 8) setMsg('Password must be at least 8 characters long');
    else if (data.name.length < 3) setMsg('Wifi name must be at least 3 characters long');
    else {
      stateManager.wsManager.sendWSMsg(controller, 'set_ap_credentials', data);
      //setWSReq(CreateControllerWSRequest(wsRef.current, id, 'set_ap_credentials', data));
      handleClose();
      setMsg('');
    }
  };
  const resetWifi = (event) => {
    if (controller.connection?.status !== 'good') return;
    let data = { password: '', name: '', reset: true };
    stateManager.wsManager.sendWSMsg(controller, 'set_ap_credentials', data);
    //setWSReq(CreateControllerWSRequest(wsRef.current, id, 'set_ap_credentials', data));
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={connectionStatus !== 'good'}
      open={open}
      onClose={handleClose}
      onEnterAction={changeWifi}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('change_wifi_options')}</DialogTitle>
      <DialogContent>
        <Typography paragraph>{t('change_wifi_options_info')}</Typography>
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label={t('label_wifi_name')}
          type="text"
          fullWidth
          onChange={(event) => setName(event.target.value)}
        />
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label={t('label_wifi_password')}
          type="password"
          fullWidth
          autoComplete="new-password"
          onChange={(event) => setPassword(event.target.value)}
        />
        {msg}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <ActionButton
          title={connectionDisabledReason}
          disabled={connectionStatus !== 'good'}
          onClick={changeWifi}
          color="primary"
        >
          {t('apply')}
        </ActionButton>
        <ActionButton
          title={connectionDisabledReason || t('reset_to_factory_default')}
          disabled={connectionStatus !== 'good'}
          onClick={resetWifi}
          color="primary"
        >
          {t('reset')}
        </ActionButton>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function ProjectDialog({ t, open, handleClose, controller }) {
  let { enqueueSnackbar } = useSnackbar();
  const [pin, setPin] = useState(null);

  const ssid = () => {
    return controller.id + '_removed_project_restrictions';
  };

  const toggleProjectRestrictions = () => {
    fetch('api/confirm_pin');
    fetch(
      'api/confirm_pin?' +
        new URLSearchParams({
          project_id: controller.stable_project_id,
          pin: pin
        }).toString()
    )
      .then((response) => {
        if (response.ok) {
          if (controller.project_id > 0) {
            sessionStorage.setItem(ssid(), true);
            controller.project_id = 0;
            enqueueSnackbar(t('removed_project_restrictions'), { variant: 'info' });
          } else {
            controller.project_id = controller.stable_project_id;
            sessionStorage.removeItem(ssid());
            enqueueSnackbar(t('enabled_project_restrictions'), { variant: 'info' });
          }
        } else {
          response.text().then((text) => enqueueSnackbar(t(text), { variant: 'error' }));
        }
      })
      .catch((_) => enqueueSnackbar(t('unknown_error'), { variant: 'error' }));
    handleClose();
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={toggleProjectRestrictions}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        {t('confirm_tgl_project_restrictions_title')}
      </DialogTitle>
      <DialogContent>
        {t('confirm_tgl_project_restrictions_text')}
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label={t('label_project_settings_pin')}
          type="password"
          fullWidth
          autoComplete="new-password"
          onChange={(event) => setPin(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={toggleProjectRestrictions} color="primary">
          {t('toggle_project_restrictions')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
